import React, { useEffect, useState } from 'react'
import { Container, ListContainer } from '@pmi/pmi-iqos-ds-react'
import {
  Assets,
  getRoute,
  formattedDescriptionJSONLD,
  Sites
} from '@pmi/pmi-iqos-shared-react'
import {
  ProductBundleDetailContent,
  ProductSelect
} from '@zyn-common/components'

const imageBlockId = 'bundle-detail-images'

const getOptionJSONLDAttributes = (
  selectedProducts,
  bundle,
  bundleDetailPath
) => {
  const bundlePrice = Object.keys(selectedProducts)
    .map(key => {
      return bundle.products[key].options[selectedProducts[key]].price
    })
    .reduce((a, b) => a + b, 0)
  const inStock = (Object.keys(selectedProducts) || []).every(
    key => bundle.products[key].options[selectedProducts[key]].inStock
  )
  return {
    name: bundle.name,
    description: formattedDescriptionJSONLD(bundle.description),
    url: bundleDetailPath
      ? getRoute(
          bundleDetailPath + bundle.id,
          true,
          false,
          null,
          null,
          Sites.zyn
        )
      : '',
    image: bundle.imagePath,
    offers: {
      price: bundlePrice,
      availability: inStock
        ? Assets.buildSchemaUrl(Assets.schemaSections.inStock)
        : Assets.buildSchemaUrl(Assets.schemaSections.outOfStock),
      url: bundleDetailPath
        ? getRoute(
            bundleDetailPath + bundle.id,
            true,
            false,
            null,
            null,
            Sites.zyn
          )
        : ''
    }
  }
}

const JSONLD = ({
  bundle,
  selectedProducts,
  bundleDetailPath,
  canonicalUrl
}) => {
  if (bundle) {
    const optionAttributes = getOptionJSONLDAttributes(
      selectedProducts,
      bundle,
      bundleDetailPath
    )
    const searchProduct = {
      ...optionAttributes,
      '@context': Assets.buildSchemaUrl(),
      '@type': 'Product',
      '@id': canonicalUrl,
      brand: {
        '@type': 'Thing',
        name: 'IQOS'
      },
      offers: {
        ...optionAttributes.offers,
        '@type': 'Offer',
        priceCurrency: 'JPY',
        itemCondition: Assets.buildSchemaUrl(Assets.schemaSections.newCondition)
      }
    }
    return (
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(searchProduct, null, ' ')
        }}
      />
    )
  }
  return null
}

const getDefaultSelectedProducts = bundle =>
  bundle.products.reduce((selectedProducts, product, index) => {
    selectedProducts[index] = product.selectedVariant
    return selectedProducts
  }, {})

const ProductBundleDetailView = ({
  bundle,
  translations,
  loading,
  loadingButton,
  extraDataLoaded,
  shortestDeliveryDate,
  backgroundColor,
  handleColorChange,
  handleAddToCart,
  defaultSelectedProducts,
  bundleDetailPath,
  canonicalUrl,
  line
}) => {
  const [highlightedProduct, setHighlightedProduct] = useState(0)
  const [selectedProducts, setSelectedProducts] = useState(
    defaultSelectedProducts || getDefaultSelectedProducts(bundle)
  )

  useEffect(() => {
    if (!defaultSelectedProducts) {
      handleReset()
    }
  }, [])

  const handleReset = () => {
    updateSelectionState(getDefaultSelectedProducts(bundle))
  }

  const updateSelectionState = selectedVariantIndexes => {
    setSelectedProducts(selectedVariantIndexes)
  }

  const onColorChange = (productIndex, optionIndex) => {
    const newSelectedProducts = { ...selectedProducts }
    newSelectedProducts[productIndex] = optionIndex
    updateSelectionState(newSelectedProducts)
    handleColorChange && handleColorChange(newSelectedProducts)
  }

  const onAddToCart = () => {
    const items = bundle.products.map((p, i) => {
      return {
        id: p.id,
        qty: 1,
        chainId: bundle.id,
        options: {
          optionId: p.options[selectedProducts[i]].id,
          variantId: p.options[selectedProducts[i]].variantId
        }
      }
    })
    handleAddToCart && handleAddToCart(items)
  }

  const handleProductSelection = newHighlightedProduct => {
    setHighlightedProduct(newHighlightedProduct)
  }

  return (
    <ListContainer columns={[1, 1, 2]} yBetween="6" xBetween="8">
      <Container fullWidth>
        <Container fullWidth id={imageBlockId}>
          {bundle && selectedProducts && (
            <ProductSelect
              products={bundle.products}
              selectedProducts={selectedProducts}
              highlightedProduct={highlightedProduct}
              onSelectProduct={handleProductSelection}
            />
          )}
        </Container>
      </Container>
      <Container fullWidth>
        {(bundle && bundle.products && selectedProducts && (
          <ProductBundleDetailContent
            currency={bundle.currency}
            products={bundle.products}
            selectedProducts={selectedProducts}
            highlightedProduct={highlightedProduct}
            loading={loading}
            loadingButton={loadingButton}
            imageBlockId={imageBlockId}
            name={bundle.name}
            badge={bundle.badge}
            shortDescription={bundle.shortDescription}
            fullDescription={bundle.fullDescription}
            descriptionLine={bundle.descriptionLine}
            onReset={handleReset}
            onAddToCart={onAddToCart}
            onColorSelected={onColorChange}
            translations={translations}
            shortestDeliveryDate={shortestDeliveryDate}
            backgroundColor={backgroundColor}
            extraDataLoaded={extraDataLoaded}
            promotionBanner={bundle.promotionBanner}
            line={line}
          />
        )) ||
          null}
      </Container>
      {bundle && bundle.products && selectedProducts && (
        <JSONLD
          bundle={bundle}
          selectedProducts={selectedProducts}
          bundleDetailPath={bundleDetailPath}
          canonicalUrl={canonicalUrl}
        />
      )}
    </ListContainer>
  )
}

export default ProductBundleDetailView
